import CustomButton from "@/components/common/button";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Textarea,
  useDisclosure,
} from "@nextui-org/react";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { IoLogIn } from "react-icons/io5";
import { useSelector } from "react-redux";
import SlideAnimationFrame from "../elements/slideAnimationFrame";
import { FaCircleCheck } from "react-icons/fa6";
import FlagIcon from "@/helpers/svg/flagIcon.svg";
import { IoClose } from "react-icons/io5";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ContentReportApi } from "@/config/cms/api";
import { enqueueSnackbar } from "notistack";
import Cookies from "js-cookie";
import { getPathname } from "@/helpers/browser.utils";

const schema = yup
  .object({
    body: yup
      .string()
      .min(5, "توضیحات باید حداقل شامل ۵ حرف باشد")
      .required("توضیحات الزامی می باشد"),
    playbackProblem: yup.string(),
    subtitleProblem: yup.string(),
    audioProblem: yup.string(),
    customReport: yup.string(),
  })
  .required();

const UserReport = ({ contentKey, data }) => {
  const { profile } = useSelector((state) => state.Auth);
  const [activeStage, setActiveStage] = useState(profile.isLogin ? 1 : 0);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isLoading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      body: "",
      customReport: false,
      playbackProblem: false,
      subtitleProblem: false,
    },
  });

  useEffect(() => {
    if (activeStage !== 1) {
      setActiveStage(profile.isLogin ? 1 : 0);
    }
  }, [profile.isLogin]);

  const handleReportSubmit = async (data) => {
    setLoading(true);
    try {
      const response =
        await ContentReportApi.prototype.contentReportControllerCreateReports(
          {
            ...data,
            customReport: data.customReport ? 1 : 0,
            playbackProblem: data.playbackProblem ? 1 : 0,
            subtitleProblem: data.subtitleProblem ? 1 : 0,
            audioProblem: data.audioProblem ? 1 : 0,
            contentKey,
          },
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
            },
          }
        );

      if (response.status === 201) {
        enqueueSnackbar(`گزارش شما با موفقیت ثبت گردید`, {
          variant: "success",
        });
        setActiveStage(2);
        reset();
      } else {
        enqueueSnackbar(response.data?.message || "خطا در هنگام ثبت گزارش", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error?.message ||
          "خطا در هنگام ثبت گزارش",
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const LoginRequiredView = () => {
    return (
      <div className="w-full min-h-[300px]">
        <ModalBody className="p-12 pb-0 dark">
          <IoLogIn size={72} className="mx-auto mb-5" color="white" />
          <h3 className="text-white text-center">
            برای ثبت گزارش کاربر ابتدا باید وارد حساب کاربری خود شوید
          </h3>
        </ModalBody>
        <ModalFooter>
          <div className="w-full">
            <Link
              className="w-full"
              href={"/auth/login?ref" + encodeURIComponent(getPathname())}
            >
              <CustomButton
                fullWidth
                label="ورود به حساب کاربری"
                color="primary"
                classNames={"min-h-[50px]"}
                textColor="text-primary-white"
              />
            </Link>
          </div>
        </ModalFooter>
      </div>
    );
  };

  const ReportForm = () => {
    return (
      <form
        onSubmit={handleSubmit(handleReportSubmit)}
        className="w-full min-h-[300px]"
      >
        <ModalBody className="p-12 pt-6 dark">
          <h3 className="text-white text-2xl"> محتوا چه مشکلی دارد؟</h3>
          <div className="flex flex-col ">
            <div className="flex flex-col justify-center items-start">
              <span className="text-primary-white text-xl mb-2">نام فیلم</span>
              <div className="w-full min-h-[70px]">
                <Input
                  variant="bordered"
                  className="h-[30px] bg-transparent w-full lg:max-w-full mb-3 text-white"
                  value={data?.title}
                  size="lg"
                  readOnly
                  disabled
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-start md:justify-between gap-5 md:gap-14 items-center">
              <Checkbox color="danger" {...register("customReport")}>
                <span className="text-primary-white text-md md:text-lg">
                  ایراد ممیزی دارد
                </span>
              </Checkbox>
              <Checkbox color="danger" {...register("playbackProblem")}>
                <span className="text-primary-white text-md md:text-lg">
                  پخش نمیشود
                </span>
              </Checkbox>
              <Checkbox color="danger" {...register("subtitleProblem")}>
                <span className="text-primary-white text-md md:text-lg ">
                  زیرنویس مشکل دارد
                </span>
              </Checkbox>
              <Checkbox color="danger" {...register("audioProblem")}>
                <span className="text-primary-white text-md md:text-lg ">
                  صدا مشکل دارد
                </span>
              </Checkbox>
            </div>
            <div className="w-full">
              <Textarea
                variant="bordered"
                placeholder="توضیحات"
                className="bg-transparent w-full lg:max-w-full my-3 text-white"
                size="lg"
                minRows={4}
                maxRows={6}
                {...register("body")}
              />
              {errors.body && (
                <span className="text-red-500 font-normal text-sm">
                  {errors.body.message}
                </span>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="w-full">
            <Button
              fullWidth
              color="primary"
              type="submit"
              className={"min-h-[50px] text-primary-white text-lg"}
              isLoading={isLoading}
            >
              ارسال گزارش
            </Button>
          </div>
        </ModalFooter>
      </form>
    );
  };

  const SubmitSuccessView = () => {
    return (
      <div className="w-full min-h-[300px]">
        <ModalBody className="p-12 pb-0 dark">
          <FaCircleCheck
            size={100}
            className="mx-auto mb-5"
            color="mediumspringgreen"
          />
          <h3 className="text-white text-center">
            باتشکر، گزارش شما ثبت گردید، همکاران ما در بخش پشتیبانی پس از بررسی
            با شما در ارتباط خواهند بود
          </h3>
        </ModalBody>
        <ModalFooter>
          <div className="w-full mt-10">
            <CustomButton
              onClick={onClose}
              fullWidth
              label="بازگشت"
              color="primary"
              classNames={"min-h-[50px]"}
              textColor="text-primary-white"
            />
          </div>
        </ModalFooter>
      </div>
    );
  };

  return (
    <>
      <Button
        radius="full"
        className="content-detail-secondary-action content-detail-report-action"
        onClick={onOpen}
      >
        <FlagIcon />
        گزارش کاربر
      </Button>
      <Modal
        backdrop="blur"
        isOpen={isOpen}
        size={profile.isLogin ? "3xl" : "lg"}
        onClose={onClose}
        className="bg-[#262626]"
        closeButton={
          <Button
            size="lg"
            variant="light"
            isIconOnly
            aria-label="بستن پنجره گزارش"
            style={{ zIndex: 999 }}
          >
            <IoClose size={24} />
          </Button>
        }
      >
        <ModalContent className="min-h-[300px]">
          <SlideAnimationFrame
            activeSectionIndex={activeStage}
            watchValue={isOpen}
            noAnimatedContainer
            instanceId="user-report-view"
            sections={[
              { id: "login-required-view", children: <LoginRequiredView /> },
              { id: "report-form-view", children: <ReportForm /> },
              { id: "submit-success-view", children: <SubmitSuccessView /> },
            ]}
          />
        </ModalContent>
      </Modal>
    </>
  );
};

export default UserReport;
